/**
 * Delete confirmation dialog for absence series
 * Provides options to delete all absences or only future absences
 */

import { useLingui } from '@lingui/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useDeleteRecurringAbsence } from '@/hooks/useEmployeeAbsences';

export interface DeleteAbsenceSeriesDialogProps {
  patternId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function DeleteAbsenceSeriesDialog({
  patternId,
  open,
  onOpenChange,
  onSuccess,
}: DeleteAbsenceSeriesDialogProps) {
  const { i18n } = useLingui();
  const deleteRecurringAbsence = useDeleteRecurringAbsence();

  const handleDeleteAll = async () => {
    try {
      await deleteRecurringAbsence.mutateAsync({
        patternId,
        scope: 'all',
      });
      onSuccess?.();
      onOpenChange(false);
    } catch {
      // Keep dialog open on error so user can see error toast and retry
    }
  };

  const handleDeleteFuture = async () => {
    try {
      await deleteRecurringAbsence.mutateAsync({
        patternId,
        scope: 'future',
      });
      onSuccess?.();
      onOpenChange(false);
    } catch {
      // Keep dialog open on error so user can see error toast and retry
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {i18n._({
              id: 'absences.series.delete.title',
              message: 'Delete Series',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {i18n._({
                  id: 'absences.series.delete.description',
                  message: 'Choose how to delete the absences in this recurring series.',
                })}
              </p>

              {/* Warning message */}
              <div className="flex items-start gap-2 rounded-md border border-warning-on-surface/40 bg-warning-muted p-3 text-warning-on-surface">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="text-sm" data-testid="warning-text">
                  {i18n._({
                    id: 'absences.series.delete.warning',
                    message: 'This action cannot be undone',
                  })}
                </span>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            variant="outline"
            className="h-auto min-h-10 w-full whitespace-normal"
            onClick={handleDeleteFuture}
            disabled={deleteRecurringAbsence.isPending}
            data-testid="delete-future-button"
          >
            {deleteRecurringAbsence.isPending && (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            )}
            {i18n._({
              id: 'absences.series.delete.future',
              message: 'Delete future absences only',
            })}
          </Button>
          <Button
            variant="destructive"
            className="h-auto min-h-10 w-full whitespace-normal"
            onClick={handleDeleteAll}
            disabled={deleteRecurringAbsence.isPending}
            data-testid="delete-all-button"
          >
            {deleteRecurringAbsence.isPending && (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            )}
            {i18n._({
              id: 'absences.series.delete.all',
              message: 'Delete all absences',
            })}
          </Button>
          {/* #2119: stacked full-width buttons, Cancel last; the German labels did not fit in one row. */}
          <AlertDialogCancel className="mt-0 w-full" data-testid="cancel-button">
            {i18n._({ id: 'common.cancel', message: 'Cancel' })}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
